import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  Checkbox, Fade, TableRow, TableCell,
} from '@material-ui/core';
import {
  chunk, compose, every,
  filter, find, get,
  getOr, identity, includes,
  isEmpty, isNull, keys,
  map, mapValues, nth,
  pickBy, reduce, size,
  some,
} from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import {
  Shimmer, Snackbar, Spinner, Table,
} from '../../../common/components';
import messages from '../../messages';
import DeleteConfirmationDialog from '../DeleteConfirmationDialog';
import DeleteReconfirmationDialog from '../DeleteReconfirmationDialog';
import TableToolBar from '../TableToolBar';
import TargetListDialog from '../TargetListDialog';

const cols = [
  { id: 'checkbox', style: css` width: 5%;` },
  { id: 'number', style: css`width: 10%;` },
  { id: 'title', style: css`width: 40%;` },
  { id: 'writer', style: css`width: 10%;` },
  { id: 'sendAt', style: css`width: 10%;` },
  { id: 'status', style: css`width: 15%;` },
  { id: 'response', style: css`width: 10%;` },
];

const S = {
  Checkbox: styled(({ classes, dark, ...otherProps }) => (
    <Checkbox
      classes={{
        ...classes,
        checked: 'checked',
      }}
      {...otherProps}
    />
  ))`
    color: ${({ dark, theme }) => theme.palette.grey[dark ? 500 : 400]};
    :hover {
      color: ${({ theme }) => theme.palette.grey[500]};
    }
    &.checked {
      color: ${({ theme }) => theme.palette.primary.light};
    }
  `,
  Link: styled(Link)`
    display: flex;
    align-items: center;
    height: 100%;
    :hover {
      text-decoration: underline;
    }
  `,
  Status: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  Mark: styled.mark`
    display: flex;
    align-items: center;
    max-height: 20px;
    padding: 0 6px 2px 6px;
    border-radius: 4px;
    color: ${({ theme }) => theme.palette.secondary.contrastText};
    background-color: ${({ active, theme }) => (active ? theme.palette.secondary.main : theme.palette.blueGrey[600])};
    font-size: 0.75rem;
    font-weight: 600;
  `,
  TargetListButton: styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    color: ${({ active, theme }) => !active && theme.palette.grey[600]};
    cursor: pointer;
    outline: none;
    :hover {
      text-decoration: underline;
    }
    :hover em {
      text-decoration: underline;
    }
  `,
  RecordCount: styled.em`
    color: ${({ active, theme }) => (active ? theme.palette.secondary.main : theme.palette.grey[600])};
    font-style: normal;
  `,
  TableRow: styled(({ classes, ...otherProps }) => (
    <TableRow
      classes={{
        ...classes,
        hover: 'hover',
      }}
      {...otherProps}
    />
  ))`
    height: 60px;
    &.hover:hover {
      background-color: ${({ theme }) => theme.palette.blueGrey[25]};
    }
  `,
  TableCell: styled(({ active, ...otherProps }) => <TableCell {...otherProps} />)`
    padding: 0;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    :nth-child(1) {
      text-align: center;
    }
    :nth-child(2) {
    }
    :nth-child(3) {
      font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    }
    :nth-child(4) {
      text-align: center;
    }
    :nth-child(5) {
      text-align: center;
    }
    :nth-child(6) {
      text-align: center;
    }
    :nth-child(7) {
      font-weight: 500;
      text-align: center;
    }
    ${({ active, theme }) => !active && css`
      && {
        color: ${theme.palette.grey[600]};
      }
    `}
    ${({ variant, theme }) => variant === 'head' && css`
      && {
        color: ${theme.palette.grey[600]};
        font-size: ${theme.typography.pxToRem(13)};
      }
    `}
  `,
  Wrapper: styled.div`
    width: 100%;
    position: relative;
  `,
  TableToolBar: styled(TableToolBar)`
    position: absolute;
    top: 0;
    left: 0;
  `,
};

const NewsletterTable = ({
  location,
  match,
  newsletters,
  targets,
  pageNumber,
  rowsPerPage,
  deletionTask,
  deleteNewsletters,
  fetchTargetsByNewsletter,
  onSend,
  initDeletionTask,
  initTargets,
}) => {
  const [open, setOpen] = useState({
    targetList: false,
    deleteConfirmation: false,
    deleteReconfirmation: false,
  });
  const [checked, setChecked] = useState(null);
  const [activeNewsletter, setActiveNewsletter] = useState(null);
  const [prevNewsletters, setPrevNewsletters] = useState({ data: null, loading: false, error: null });

  const checkedAll = useCallback(compose(
    every(identity),
    pickBy((_, key) => {
      const rows = compose(
        map(item => item.id),
        nth(pageNumber - 1),
        chunk(rowsPerPage),
      )(newsletters.data);

      return includes(key, rows);
    }),
  ), [newsletters.data, pageNumber, rowsPerPage]);

  const toggleAllChecked = useCallback(() => {
    const nids = compose(
      map(newsletter => newsletter.id),
      nth(pageNumber - 1),
      chunk(rowsPerPage),
    )(newsletters.data);

    setChecked(prevChecked => (every(identity, prevChecked)
      ? mapValues(() => false, prevChecked)
      : {
        ...prevChecked,
        ...reduce((acc, cur) => ({
          ...acc,
          [cur]: !checkedAll(prevChecked),
        }), {}, nids),
      }));
  }, [checkedAll, newsletters.data, pageNumber, rowsPerPage]);

  const checkAll = useCallback(() => setChecked(mapValues(() => true)), []);
  const uncheckAll = useCallback(() => setChecked(mapValues(() => false)), []);

  const handleDeleteConfirmationDialogOpen = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, deleteConfirmation: true })), []);
  const handleDeleteConfirmationDialogClose = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, deleteConfirmation: false })), []);
  const handleDeleteReconfirmationDialogOpen = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, deleteReconfirmation: true })), []);
  const handleDeleteReconfirmationDialogClose = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, deleteReconfirmation: false })), []);
  const handleTargetListDialogOpen = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, targetList: true })), []);
  const handleTargetListDialogClose = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, targetList: false })), []);
  const handleClick = useCallback((event) => {
    setActiveNewsletter(event.currentTarget.dataset.nid);

    handleTargetListDialogOpen();
  }, [handleTargetListDialogOpen]);

  const getRow = useCallback(newsletter => ({
    id: newsletter.id,
    cells: [
      {
        id: 'checkbox',
        content: (
          <S.Checkbox
            color="primary"
            checked={getOr(false, newsletter.id, checked)}
            onChange={() => setChecked(prevChecked => ({
              ...prevChecked,
              [newsletter.id]: !prevChecked[newsletter.id],
            }))}
          />
        ),
      },
      {
        id: 'number',
        content: get('year', newsletter.number) && get('issueNumber', newsletter.number) ? (
          <FormattedMessage
            {...messages.newsletterTableBodyNumber}
            values={{
              year: newsletter.number.year,
              issueNumber: newsletter.number.issueNumber,
            }}
          />
        ) : '-',
      },
      {
        id: 'title',
        content: (
          <S.Link
            to={{
              pathname: `/schools/${match.params.sid}/school-newsletters/${newsletter.id}`,
              state: { from: location.pathname },
            }}
          >
            {newsletter.title}
          </S.Link>
        ),
      },
      { id: 'writer', content: newsletter.writer },
      {
        id: 'sendAt',
        content: dayjs(
          newsletter.sendAt || newsletter.startAt || newsletter.createdAt.toDate(),
        ).format('YYYY.MM.DD.'),
      },
      {
        id: 'status',
        content: (
          <S.Status>
            <S.Mark active={newsletter.status === 'ongoing'}>
              <FormattedMessage
                {...messages[
                  newsletter.status === 'ongoing'
                    ? 'newslettersMarkOngoing'
                    : 'newslettersMarkClosed'
                ]}
              />
            </S.Mark>
          </S.Status>
        ),
      },
      {
        id: 'response',
        content: (
          <S.TargetListButton
            type="button"
            active={newsletter.status === 'ongoing'}
            data-nid={newsletter.id}
            onClick={handleClick}
          >
            <S.RecordCount active={newsletter.status === 'ongoing'}>
              {newsletter.recordCount}
            </S.RecordCount>
            /
            {newsletter.targetCount}
          </S.TargetListButton>
        ),
      },
    ],
    active: newsletter.status === 'ongoing',
  }), [checked, handleClick, location.pathname, match.params.sid]);

  const head = useMemo(() => ({
    id: 'head',
    cells: [
      {
        id: 'checkbox',
        content: (
          <S.Checkbox
            color="primary"
            checked={
              !isNull(checked)
              && !isEmpty(checked)
              && checkedAll(checked)
            }
            onChange={toggleAllChecked}
            dark
          />
        ),
      },
      { id: 'number', content: <FormattedMessage {...messages.newsletterTableHeadNumber} /> },
      { id: 'title', content: <FormattedMessage {...messages.newsletterTableHeadTitle} /> },
      { id: 'writer', content: <FormattedMessage {...messages.newsletterTableHeadWriter} /> },
      { id: 'sendAt', content: <FormattedMessage {...messages.newsletterTableHeadSendAt} /> },
      { id: 'status', content: <FormattedMessage {...messages.newsletterTableHeadStatus} /> },
      { id: 'response', content: <FormattedMessage {...messages.newsletterTableHeadResponse} /> },
    ],
  }), [checked, checkedAll, toggleAllChecked]);

  const body = useMemo(() => ({
    rows: compose(
      map(getRow),
      nth(pageNumber - 1),
      chunk(rowsPerPage),
    )(newsletters.data),
  }), [getRow, newsletters.data, pageNumber, rowsPerPage]);

  if (newsletters.data !== prevNewsletters.data) {
    setChecked(reduce((acc, cur) => ({
      ...acc, [cur.id]: false,
    }), {}, newsletters.data));

    setPrevNewsletters(newsletters);
  }

  const counts = useMemo(() => ({
    checked: compose(
      size,
      pickBy((value, key) => {
        const closedNewsletters = compose(
          map(newsletter => newsletter.id),
          filter(newsletter => newsletter.status === 'closed'),
        )(newsletters.data);

        return value && includes(key, closedNewsletters);
      }),
    )(checked),
    total: compose(
      size,
      filter(newsletter => newsletter.status === 'closed'),
    )(newsletters.data),
  }), [checked, newsletters.data]);

  const nids = useMemo(() => compose(
    keys,
    pickBy((value, key) => {
      const closedNewsletters = compose(
        map(newsletter => newsletter.id),
        filter(newsletter => newsletter.status !== 'ongoing' && newsletter.status !== 'reserved'),
      )(newsletters.data);

      return value && includes(key, closedNewsletters);
    }),
  )(checked), [checked, newsletters.data]);

  const resendNewsletterNotification = useCallback(() => {
    const newsletter = find(n => n.id === activeNewsletter, newsletters.data);

    onSend({
      newsletter,
      sid: match.params.sid,
      targets: pickBy(target => !target.recorded, targets.data),
    });
  }, [onSend, activeNewsletter, newsletters.data, match.params.sid, targets.data]);

  useEffect(() => {
    if (deletionTask.data) {
      const timeoutId = setTimeout(initDeletionTask, 3000);

      return () => clearTimeout(timeoutId);
    }

    return () => {};
  }, [deletionTask.data, initDeletionTask]);

  return (
    <>
      <S.Wrapper>
        {newsletters.loading
          ? <Shimmer />
          : (
            <>
              <Table cols={cols} head={head} body={body} tableRow={S.TableRow} tableCell={S.TableCell} />
              {some(identity, checked) && (
                <S.TableToolBar
                  checkAll={checkAll}
                  uncheckAll={uncheckAll}
                  checked={checked}
                  checkedAll={checkedAll}
                  counts={counts}
                  toggleAllChecked={toggleAllChecked}
                  onOpen={handleDeleteConfirmationDialogOpen}
                />
              )}
            </>
          )}
      </S.Wrapper>
      <TargetListDialog
        newsletter={find(n => n.id === activeNewsletter, newsletters.data)}
        targets={targets}
        onSend={resendNewsletterNotification}
        open={open.targetList}
        onClose={handleTargetListDialogClose}
        onFetch={fetchTargetsByNewsletter}
        onExited={initTargets}
      />
      <DeleteConfirmationDialog
        counts={counts}
        open={open.deleteConfirmation}
        onClose={handleDeleteConfirmationDialogClose}
        onDelete={handleDeleteReconfirmationDialogOpen}
      />
      <DeleteReconfirmationDialog
        nids={nids}
        counts={counts}
        open={open.deleteReconfirmation}
        onClose={handleDeleteReconfirmationDialogClose}
        onDelete={deleteNewsletters}
      />
      <Snackbar
        open={!!deletionTask.data}
        TransitionComponent={Fade}
        ContentProps={{
          'aria-describedby': 'delete-success-message',
        }}
        message={(
          <span id="delete-success-message">
            <FormattedMessage {...messages.newsletterTableSnackbar} />
          </span>
        )}
      />
      {deletionTask.loading && <Spinner />}
    </>
  );
};

NewsletterTable.propTypes = {
  deleteNewsletters: PropTypes.func.isRequired,
  fetchTargetsByNewsletter: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  initDeletionTask: PropTypes.func.isRequired,
  initTargets: PropTypes.func.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      sid: PropTypes.string,
    }).isRequired,
  }).isRequired,
  newsletters: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        number: PropTypes.shape({
          year: PropTypes.number,
          issueNumber: PropTypes.string,
        }),
        recordCount: PropTypes.number.isRequired,
        targetCount: PropTypes.number.isRequired,
        status: PropTypes.string.isRequired,
        startAt: PropTypes.oneOfType([
          PropTypes.shape({
            seconds: PropTypes.number.isRequired,
            nanoseconds: PropTypes.number.isRequired,
            toDate: PropTypes.func.isRequired,
          }),
          PropTypes.number,
        ]),
      }),
    ),
  }),
  targets: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    data: PropTypes.objectOf(PropTypes.shape({
      cid: PropTypes.number,
      clazzGroup: PropTypes.shape({
        id: PropTypes.number,
        grade: PropTypes.number,
        clazzName: PropTypes.string,
      }),
      clazzNumber: PropTypes.number,
      name: PropTypes.string,
      recorded: PropTypes.bool,
      customGrojups: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        isUnspecifiedGroup: PropTypes.bool,
      })),
    })),
  }).isRequired,
  pageNumber: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.string.isRequired,
  deletionTask: PropTypes.shape({
    data: PropTypes.bool,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.shape({}),
  }).isRequired,
};

NewsletterTable.defaultProps = {
  newsletters: null,
};

export default withRouter(NewsletterTable);
